import {
  TeammateAvatarEyes,
  TeammateAvatarShape,
} from './teammate-avatar.config';

interface AnimationState {
  x: number;
  y: number;
  rotation: number;
}

export interface ShapeDefinition {
  viewBox: string;
  width: number;
  height: number;
  innerRotation: number;
  regularPath: string;
  hoverState: AnimationState;
  /**
   * Default shape position in the cropped frame, defined for the Small (48 px)
   * baseline. Scale by (frameSize / 48) for x-small and large.
   */
  croppedDefaultState: AnimationState;
  /**
   * Hover shape position in the cropped frame, defined for the Small (48 px)
   * baseline. Scale by (frameSize / 48) for x-small and large.
   */
  croppedHoverState: AnimationState;
  /**
   * Visual size of the shape SVG in the cropped frame at Small (48 px) baseline.
   * Derived from the Figma TeammateShape inset (inset-[11.11%] or inset-[12.5%])
   * scaled from the 72 px base to the 64 px forced container:
   *   pentagon / circle : 56 × (64/72) ≈ 50 px
   *   square / losange  : 54 × (64/72) ≈ 48 px
   *   hexagon           : 60 × (64/72) ≈ 53 px  (image width before 90° rotation)
   */
  croppedContentSize: number;
  /**
   * Eye centre offset from the 64 px wrapper centre in the cropped frame,
   * computed from the per-shape eye positions in the 72 px spec frame:
   *   offsetX = (eyeSpecX + eyeHalfWidth) × (64/72) − 32
   *   offsetY = (eyeSpecY + eyeHalfHeight) × (64/72) − 32
   * where eyeSpec{X,Y} is the top-left of the 28×24 eye container in the spec.
   */
  croppedEyeOffsetX: number;
  croppedEyeOffsetY: number;
}

interface EyeDefinitionBase {
  viewBox: string;
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

interface SingleEyeDefinition extends EyeDefinitionBase {
  split?: false;
  regularPath: string;
  hoverPath: string;
}

interface SplitEyeDefinition extends EyeDefinitionBase {
  split: true;
  leftRegularPath: string;
  leftHoverPath: string;
  rightRegularPath: string;
  rightHoverPath: string;
}

export type EyeDefinition = SingleEyeDefinition | SplitEyeDefinition;

const PENTAGON_PATH =
  'M31.286 2.03715C34.892 -0.679052 39.7747 -0.67905 43.3806 2.03715L70.425 22.4087C74.0309 25.1249 75.5398 29.9395 74.1624 34.3344L63.8324 67.2962C62.4551 71.6911 58.5048 74.6667 54.0477 74.6667H20.619C16.1618 74.6667 12.2116 71.6911 10.8343 67.2962L0.504246 34.3344C-0.873091 29.9395 0.635772 25.1249 4.24169 22.4087L31.286 2.03715Z';

const SQUARE_PATH =
  'M0 16C0 7.16344 7.16344 0 16 0H56C64.8366 0 72 7.16344 72 16V56C72 64.8366 64.8366 72 56 72H16C7.16344 72 0 64.8366 0 56V16Z';

const CIRCLE_PATH =
  'M37.3333 0C57.9519 0 74.6667 16.7148 74.6667 37.3333C74.6667 57.9519 57.9519 74.6667 37.3333 74.6667C16.7148 74.6667 0 57.9519 0 37.3333C0 16.7148 16.7148 0 37.3333 0Z';

const HEXAGON_PATH =
  'M28.9482 1.60194C32.4868 -0.533982 36.8466 -0.533981 40.3852 1.60194L63.6148 15.6234C67.1535 17.7593 69.3333 21.7067 69.3333 25.9785V54.0215C69.3333 58.2933 67.1535 62.2407 63.6148 64.3766L40.3852 78.3981C36.8466 80.534 32.4868 80.534 28.9482 78.3981L5.71852 64.3766C2.17989 62.2407 0 58.2933 0 54.0215V25.9785C0 21.7067 2.17989 17.7593 5.71852 15.6234L28.9482 1.60194Z';

// Eye variant '1' — two dots (no hover change)
const DOTS_EYES_PATH =
  'M2.66602 0C3.04186 0 3.40212 0.113692 3.72949 0.324219C4.05156 0.531402 4.34307 0.83391 4.58594 1.21094C5.04567 1.92469 5.33301 2.91033 5.33301 4C5.33301 5.08967 5.04567 6.07531 4.58594 6.78906C4.34308 7.16609 4.05156 7.4686 3.72949 7.67578C3.40212 7.88631 3.04186 8 2.66602 8C2.29029 7.99993 1.9308 7.88625 1.60352 7.67578C1.2814 7.46862 0.989971 7.16611 0.74707 6.78906C0.287333 6.07531 1.25773e-07 5.08967 0 4C0 2.91033 0.287333 1.92469 0.74707 1.21094C0.989971 0.833889 1.28139 0.53138 1.60352 0.324219C1.9308 0.113753 2.29029 6.61392e-05 2.66602 0ZM16 0C16.3757 6.50812e-05 16.7352 0.113752 17.0625 0.324219C17.3846 0.531379 17.676 0.833889 17.9189 1.21094C18.3787 1.92469 18.666 2.91033 18.666 4C18.666 5.08967 18.3787 6.07531 17.9189 6.78906C17.676 7.16611 17.3846 7.46862 17.0625 7.67578C16.7352 7.88625 16.3757 7.99993 16 8C15.6242 8 15.2639 7.88631 14.9365 7.67578C14.6145 7.4686 14.3229 7.16609 14.0801 6.78906C13.6203 6.07531 13.333 5.08967 13.333 4C13.333 2.91033 13.6203 1.92469 14.0801 1.21094C14.3229 0.83391 14.6145 0.531403 14.9365 0.324219C15.2639 0.113692 15.6242 0 16 0Z';

// Eye variant '2' — wink (split: left=dash, right=wink on hover)
const WINK_LEFT_REGULAR_PATH =
  'M8.5 10C9.328428 10 10 10.89543 10 12C9.999865 13.10442 9.328345 14 8.5 14H5.5C4.671753 13.99985 4.000132 13.10432 4 12C4 10.895526 4.671672 10.000155 5.5 10H8.5Z';
const WINK_LEFT_HOVER_PATH =
  'M8.5 10C9.32843 10 10 10.6716 10 11.5C10 12.3284 9.32843 13 8.5 13H5.5C4.671783 12.9998 4 12.3283 4 11.5C4 10.6717 4.671783 10.0002 5.5 10H8.5Z';
const WINK_RIGHT_REGULAR_PATH =
  'M22.50025 10C23.328625 10 24.0001 10.89558 24.00025 12C24.00025 13.10457 23.3287 14 22.50025 14H19.50025C18.671875 13.99988 18.00025 13.10449 18.00025 12C18.0004 10.895656 18.67195 10.000123 19.50025 10H22.50025Z';
const WINK_RIGHT_HOVER_PATH =
  'M21.00025 7C21.828625 7 22.5001 7.67157 22.50025 8.5V11.5C22.50025 12.32843 21.8287 13 21.00025 13C20.171875 13 19.50025 12.32843 19.50025 11.5V8.5C19.5004 7.67157 20.17195 7 21.00025 7ZM20.9995 0C22.0732 0 23.1046 0.412863 23.866225 1.14844C24.434875 1.69776 24.81865 2.39419 24.9766 3.14648C25.130725 3.88112 24.48325 4.3905 23.8699 4.39063C23.196625 4.39049 22.75615 3.86396 22.590325 3.40137C22.5085 3.17319 22.372975 2.96311 22.191925 2.78809C21.877 2.48389 21.448225 2.31055 20.9995 2.31055C20.550925 2.31055 20.122675 2.48389 19.807825 2.78809C19.6267 2.96311 19.49125 3.17319 19.409425 3.40137C19.2436 3.86396 18.803125 4.39049 18.12985 4.39063C17.516425 4.3905 16.868275 3.88112 17.022475 3.14648C17.180425 2.39419 17.564875 1.69776 18.133525 1.14844C18.895075 0.412863 19.92595 0 20.9995 0Z';

// Eye variant '3' — downward curves (regular) → upward curves (hover)
const CLOSED_EYES_PATH =
  'M21.14194 14.666505C19.83793 14.666421 18.58545 14.165492 17.66049 13.271975C16.96984 12.604725 16.50363 11.758995 16.31186 10.845215C16.12469 9.952905 16.91152 9.333515 17.65659 9.333495C18.47428 9.333665 19.00886 9.973805 19.2103 10.535645C19.30972 10.812995 19.47451 11.068075 19.69467 11.280765C20.07705 11.650145 20.59711 11.860755 21.14194 11.860845C21.68688 11.860845 22.20772 11.650225 22.59018 11.280765C22.81026 11.068105 22.97516 10.812935 23.07455 10.535645C23.27602 9.973835 23.81061 9.333665 24.62826 9.333495C25.373252 9.333695 26.159213 9.953005 25.972015 10.845215C25.780224 11.759035 25.314095 12.604715 24.62338 13.271975C23.69837 14.16545 22.44597 14.666505 21.14194 14.666505ZM6.8597 14.666505C5.5557 14.666505 4.3033 14.165446 3.3783 13.271975C2.6875 12.604705 2.2204 11.759065 2.0287 10.845215C1.8416 9.953065 2.6284 9.333695 3.3734 9.333495C4.191 9.333665 4.7256 9.973835 4.9271 10.535645C5.0265 10.812935 5.1914 11.068105 5.4115 11.280765C5.7939 11.650225 6.3148 11.860845 6.8597 11.860845C7.4045 11.860755 7.9246 11.650145 8.307 11.280765C8.5271 11.068075 8.6919 10.812995 8.7914 10.535645C8.9928 9.973805 9.5274 9.333665 10.3451 9.333495C11.0901 9.333525 11.877 9.952915 11.6898 10.845215C11.498 11.758995 11.0318 12.604725 10.3412 13.271975C9.4162 14.16549 8.1637 14.666419 6.8597 14.666505Z';

const CLOSED_EYES_HOVER_PATH =
  'M6.85805 9.333495C8.16208 9.333503 9.41448 9.834544 10.33949 10.728025C11.03014 11.395225 11.4963 12.241055 11.68812 13.154785C11.87535 14.047145 11.08849 14.666505 10.3434 14.666505C9.52584 14.666205 8.9911 14.026135 8.78969 13.464355C8.69028 13.187235 8.52626 12.931795 8.30629 12.719235C7.92384 12.349785 7.40298 12.140145 6.85805 12.140135C6.3131 12.140135 5.79226 12.349775 5.4098 12.719235C5.18982 12.931795 5.02582 13.187225 4.9264 13.464355C4.72499 14.026115 4.19021 14.666165 3.37269 14.666505C2.627599 14.666505 1.840739 14.047145 2.027967 13.154785C2.219792 12.241055 2.685951 11.395215 3.3766 10.728025C4.30161 9.834557 5.55402 9.333495 6.85805 9.333495ZM21.1412 9.333495C22.4452 9.333584 23.6977 9.834518 24.6227 10.728025C25.3134 11.395235 25.7795 12.241035 25.9713 13.154785C26.1585 14.047125 25.3717 14.666475 24.6266 14.666505C23.8089 14.666335 23.2743 14.026195 23.0729 13.464355C22.9735 13.187115 22.8086 12.931855 22.5885 12.719235C22.2062 12.349955 21.686 12.140225 21.1412 12.140135C20.5963 12.140135 20.0755 12.349785 19.693 12.719235C19.4729 12.931875 19.3081 13.187075 19.2086 13.464355C19.0072 14.026195 18.4726 14.666335 17.6549 14.666505C16.9099 14.666315 16.123 14.047005 16.3102 13.154785C16.502 12.241005 16.9691 11.395255 17.6598 10.728025C18.5848 9.834655 19.8373 9.333495 21.1412 9.333495Z';

// Eye variant '4' — vertical lines (no hover change)
const LINES_EYES_PATH =
  'M2 0C3.10457 0 4 0.895431 4 2V8.66699C3.99982 9.77141 3.10446 10.667 2 10.667C0.895558 10.667 0.000175415 9.7714 0 8.66699V2C1.4066e-06 0.895446 0.89545 2.23875e-05 2 0ZM14 0C15.1044 0.000175217 16 0.895539 16 2V8.66699C15.9998 9.7713 15.1043 10.6668 14 10.667C12.8955 10.667 12.0002 9.77141 12 8.66699V2C12 0.895432 12.8954 0 14 0Z';

// Eye variant '5' — upward arcs (regular) → dots (hover)
const SMILE_EYES_PATH =
  'M4.85899 0C6.16298 9.02664e-05 7.41548 0.501019 8.34043 1.39453C9.03107 2.06178 9.4973 2.90751 9.68906 3.82129C9.87623 4.71358 9.08939 5.33297 8.34434 5.33301C7.52664 5.33284 6.99207 4.6927 6.79063 4.13086C6.69121 3.85351 6.52641 3.59843 6.30625 3.38574C5.92388 3.01636 5.4038 2.80575 4.85899 2.80566C4.31404 2.80566 3.7932 3.01628 3.41074 3.38574C3.19065 3.59841 3.02577 3.85357 2.92637 4.13086C2.72491 4.69268 2.19031 5.33284 1.37266 5.33301C0.627699 5.33282 -0.159166 4.71345 0.0279313 3.82129C0.219715 2.90744 0.686812 2.0618 1.37754 1.39453C2.30255 0.501064 3.55496 -8.36261e-08 4.85899 0ZM19.1412 0C20.4452 8.36267e-08 21.6976 0.501051 22.6227 1.39453C23.3134 2.06179 23.7795 2.90747 23.9713 3.82129C24.1585 4.7135 23.3725 5.3328 22.6275 5.33301C21.8098 5.33284 21.2743 4.6927 21.0729 4.13086C20.9734 3.85366 20.8095 3.59835 20.5895 3.38574C20.207 3.01628 19.6862 2.80566 19.1412 2.80566C18.5964 2.80574 18.0763 3.01636 17.6939 3.38574C17.4738 3.59843 17.309 3.85351 17.2096 4.13086C17.0081 4.6927 16.4736 5.33284 15.6559 5.33301C14.9108 5.33299 14.124 4.7136 14.3111 3.82129C14.5029 2.9075 14.9691 2.06178 15.6598 1.39453C16.5847 0.50101 17.8372 7.96776e-05 19.1412 0Z';

export const SHAPE_DEFINITIONS: Record<TeammateAvatarShape, ShapeDefinition> = {
  pentagon: {
    viewBox: '0 0 74.6667 74.6667',
    width: 74.667,
    height: 74.667,
    innerRotation: 0,
    regularPath: PENTAGON_PATH,
    hoverState: {
      x: -3,
      y: -0.77,
      rotation: -4,
    },
    croppedDefaultState: { x: -16, y: 0, rotation: 0 },
    croppedHoverState: { x: -16.15, y: -4.15, rotation: -4 },
    // 56 px shape × (64/72) = 49.78 px — spec §4 eye at (29, 27) in 72 px → centre (43, 39) → ×0.889 → offset (+6.22, +2.67) from 64 px wrapper centre
    croppedContentSize: 50,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: 2.67,
  },
  square: {
    viewBox: '0 0 72 72',
    width: 72,
    height: 72,
    innerRotation: 0,
    regularPath: SQUARE_PATH,
    hoverState: {
      x: 0,
      y: 4,
      rotation: 0,
    },
    croppedDefaultState: { x: -18, y: 2, rotation: 0 },
    croppedHoverState: { x: -16, y: 0, rotation: 0 },
    // 54 px shape × (64/72) = 48 px — spec §4 eye at (29, 26) → centre (43, 38) → ×0.889 → offset (+6.22, +1.78)
    croppedContentSize: 48,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: 1.78,
  },
  circle: {
    viewBox: '0 0 74.6667 74.6667',
    width: 74.667,
    height: 74.667,
    innerRotation: 0,
    regularPath: CIRCLE_PATH,
    hoverState: {
      x: 0,
      y: 4,
      rotation: 0,
    },
    croppedDefaultState: { x: -18, y: 2, rotation: 0 },
    croppedHoverState: { x: -16, y: 0, rotation: 0 },
    // 56 px shape × (64/72) = 49.78 px — spec §4 eye at (29, 18) → centre (43, 30) → ×0.889 → offset (+6.22, -5.33)
    // Circle eyes are positioned significantly higher than other shapes (Y=18 vs Y=26–27)
    croppedContentSize: 50,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: -5.33,
  },
  hexagon: {
    viewBox: '0 0 69.3333 80',
    width: 69.333,
    height: 80,
    innerRotation: 90,
    regularPath: HEXAGON_PATH,
    hoverState: {
      x: -6,
      y: -2.21,
      rotation: -8,
    },
    croppedDefaultState: { x: -18, y: 2, rotation: 0 },
    croppedHoverState: { x: -20.15, y: -4.15, rotation: -4 },
    // Figma image 60 px wide × (64/72) = 53.33 px — spec §4 eye at (30, 26) → centre (44, 38) → ×0.889 → offset (+7.11, +1.78)
    croppedContentSize: 53,
    croppedEyeOffsetX: 7.11,
    croppedEyeOffsetY: 1.78,
  },
  losange: {
    viewBox: '0 0 72 72',
    width: 72,
    height: 72,
    innerRotation: 45,
    regularPath: SQUARE_PATH,
    hoverState: {
      x: -6.21,
      y: -2.21,
      rotation: -8,
    },
    croppedDefaultState: { x: -22, y: 4, rotation: 0 },
    croppedHoverState: { x: -24.14, y: -0.14, rotation: -8 },
    // 54 px square (before 45° rotation) × (64/72) = 48 px — spec §4 eye at (29, 25) → centre (43, 37) → ×0.889 → offset (+6.22, +0.89)
    croppedContentSize: 48,
    croppedEyeOffsetX: 6.22,
    croppedEyeOffsetY: 0.89,
  },
};

export const EYE_DEFINITIONS: Record<TeammateAvatarEyes, EyeDefinition> = {
  '1': {
    viewBox: '0 0 18.666 8',
    width: 18.666,
    height: 8,
    offsetX: 8,
    offsetY: 4,
    regularPath: DOTS_EYES_PATH,
    hoverPath: DOTS_EYES_PATH,
  },
  '2': {
    viewBox: '0 0 28 24',
    width: 28,
    height: 24,
    offsetX: 8,
    offsetY: 2.67,
    split: true,
    leftRegularPath: WINK_LEFT_REGULAR_PATH,
    leftHoverPath: WINK_LEFT_HOVER_PATH,
    rightRegularPath: WINK_RIGHT_REGULAR_PATH,
    rightHoverPath: WINK_RIGHT_HOVER_PATH,
  },
  '3': {
    viewBox: '0 0 28 24',
    width: 28,
    height: 24,
    offsetX: 8,
    offsetY: 1.33,
    regularPath: CLOSED_EYES_PATH,
    hoverPath: CLOSED_EYES_HOVER_PATH,
  },
  '4': {
    viewBox: '0 0 16 10.667',
    width: 16,
    height: 10.667,
    offsetX: 8,
    offsetY: 2.67,
    regularPath: LINES_EYES_PATH,
    hoverPath: LINES_EYES_PATH,
  },
  '5': {
    viewBox: '0 0 24 8',
    width: 24,
    height: 8,
    offsetX: 8,
    offsetY: -8,
    regularPath: SMILE_EYES_PATH,
    hoverPath: DOTS_EYES_PATH,
  },
};
